/**
 * Server functions for settings overview statistics
 *
 * Provides authenticated endpoints for fetching dashboard statistics
 * about stores, projects, configuration versions, users, and audit events.
 */
import { createServerFn } from "@tanstack/react-start"
import { getRequest } from "@tanstack/react-start/server"
import { count, sql, gte, eq, isNull } from "drizzle-orm"
import { user } from "@/db/auth-schema"
import { db } from "@/db/db"
import { stores, countries, projectsTable, auditEvents } from "@/db/schema"
import {
  configurationVersions,
  configCategories,
  configBandwidths,
} from "@/db/store-configuration-schema"
import { assertPermission } from "@/lib/assert-permission.server"
import { authFunctionMiddleware } from "@/lib/auth-middleware"

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type SettingsOverviewStats = {
  /** Total number of stores in the system */
  totalStores: number
  /** Number of countries with stores */
  totalCountries: number
  /** Number of layout projects */
  totalProjects: number
  /** Number of active configuration versions */
  activeConfigVersions: number
  /** Total number of configuration versions (non-archived) */
  totalConfigVersions: number
  /** Number of registered users */
  totalUsers: number
  /** Number of audit events in the last 7 days */
  recentAuditEvents: number
  /** Number of categories in active config */
  activeCategories: number
  /** Number of merchandise groups (bandwidths) in active config */
  activeBandwidths: number
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal Helpers
// ─────────────────────────────────────────────────────────────────────────────

async function fetchSettingsStats(): Promise<SettingsOverviewStats> {
  // Run all queries in parallel for efficiency
  const sevenDaysAgo = new Date()
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

  const [
    storesResult,
    countriesResult,
    projectsResult,
    configVersionsResult,
    usersResult,
    auditResult,
    activeConfigResult,
  ] = await Promise.all([
    // Count stores (respecting user's store access restrictions)
    db
      .select({ count: count() })
      .from(stores)
      .then((r) => r[0]?.count ?? 0),

    // Count countries
    db
      .select({ count: count() })
      .from(countries)
      .then((r) => r[0]?.count ?? 0),

    // Count projects
    db
      .select({ count: count() })
      .from(projectsTable)
      .then((r) => r[0]?.count ?? 0),

    // Count config versions (active and total non-archived)
    db
      .select({
        total: count(),
        active: sql<number>`count(*) filter (where ${configurationVersions.isActive} = true)`,
      })
      .from(configurationVersions)
      .where(isNull(configurationVersions.archivedAt))
      .then((r) => ({
        total: r[0]?.total ?? 0,
        active: Number(r[0]?.active ?? 0),
      })),

    // Count users
    db
      .select({ count: count() })
      .from(user)
      .then((r) => r[0]?.count ?? 0),

    // Count audit events in the last 7 days
    db
      .select({ count: count() })
      .from(auditEvents)
      .where(gte(auditEvents.occurredAt, sevenDaysAgo))
      .then((r) => r[0]?.count ?? 0),

    // Get active config's categories and bandwidths count
    db.query.configurationVersions.findFirst({
      where: eq(configurationVersions.isActive, true),
      columns: { id: true },
    }),
  ])

  // Get category and bandwidth counts from active config
  let activeCategories = 0
  let activeBandwidths = 0

  if (activeConfigResult?.id) {
    const [categoriesCount, bandwidthsCount] = await Promise.all([
      db
        .select({ count: count() })
        .from(configCategories)
        .where(eq(configCategories.configVersionId, activeConfigResult.id))
        .then((r) => r[0]?.count ?? 0),
      db
        .select({ count: count() })
        .from(configBandwidths)
        .where(eq(configBandwidths.configVersionId, activeConfigResult.id))
        .then((r) => r[0]?.count ?? 0),
    ])
    activeCategories = categoriesCount
    activeBandwidths = bandwidthsCount
  }

  return {
    totalStores: storesResult,
    totalCountries: countriesResult,
    totalProjects: projectsResult,
    activeConfigVersions: configVersionsResult.active,
    totalConfigVersions: configVersionsResult.total,
    totalUsers: usersResult,
    recentAuditEvents: auditResult,
    activeCategories,
    activeBandwidths,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Server Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch settings overview statistics
 *
 * Returns counts of various entities in the system for display
 * on the settings dashboard.
 */
export const getSettingsOverviewStatsFn = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async () => {
    const request = getRequest()
    // Require at least read permission on settings/config
    await assertPermission(request, "configVersion", "read")

    return fetchSettingsStats()
  })
